import { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '../api/client';

/**
 * Lightweight cache for State records (loaded once per session).
 * Falls back to an empty array if the endpoint fails.
 */
let statesCache = null;
let statesPromise = null;

export function useStates() {
  const [states, setStates] = useState(statesCache || []);
  const [loading, setLoading] = useState(!statesCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    async function load() {
      if (statesCache) {
        setStates(statesCache);
        setLoading(false);
        return;
      }
      if (!statesPromise) {
        statesPromise = adminApi
          .get('/admin/states', { params: { page: 1, page_size: 200 } })
          .then((res) => {
            statesCache = res.data.items || [];
            return statesCache;
          })
          .catch((e) => {
            statesPromise = null;
            throw e;
          });
      }
      try {
        const items = await statesPromise;
        if (active) {
          setStates(items);
          setLoading(false);
        }
      } catch (e) {
        if (active) {
          setError(e);
          setLoading(false);
        }
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const refresh = useCallback(async () => {
    statesCache = null;
    statesPromise = null;
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/states', { params: { page: 1, page_size: 200 } });
      statesCache = res.data.items || [];
      setStates(statesCache);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helpers
  function findByAbbr(abbr) {
    if (!abbr) return null;
    const u = abbr.toUpperCase();
    return states.find((s) => s.abbreviation === u) || null;
  }
  function findById(id) {
    if (!id) return null;
    return states.find((s) => s.id === id) || null;
  }

  return { states, loading, error, refresh, findByAbbr, findById };
}

/**
 * Fetches counties for a given stateId. Pass null/undefined to reset.
 */
const countiesCache = new Map(); // stateId -> array

export function useCounties(stateId) {
  const [counties, setCounties] = useState(stateId ? (countiesCache.get(stateId) || []) : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastReq = useRef(0);

  useEffect(() => {
    if (!stateId) {
      setCounties([]);
      return;
    }
    if (countiesCache.has(stateId)) {
      setCounties(countiesCache.get(stateId));
      return;
    }
    const reqId = ++lastReq.current;
    setLoading(true);
    setError(null);
    adminApi
      .get(`/admin/states/${stateId}/counties`, { params: { page: 1, page_size: 200 } })
      .then((res) => {
        if (reqId !== lastReq.current) return;
        const items = res.data.items || [];
        countiesCache.set(stateId, items);
        setCounties(items);
      })
      .catch((e) => {
        if (reqId !== lastReq.current) return;
        setError(e);
        setCounties([]);
      })
      .finally(() => {
        if (reqId === lastReq.current) setLoading(false);
      });
  }, [stateId]);

  return { counties, loading, error };
}

export function invalidateCountiesCache(stateId) {
  if (stateId) countiesCache.delete(stateId);
  else countiesCache.clear();
}
